import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Plus, 
  FileText, 
  Star, 
  Folder, 
  Search, 
  RefreshCw, 
  FolderOpen, 
  MoreVertical, 
  MoreHorizontal,
  Settings, 
  BookOpen, 
  List,
  Bookmark,
  Search as SearchIcon,
  X as XMarkIcon,
  Edit3,
  Trash2,
  Clock,
  Grid,
  Archive,
  Pin,
  Shield,
  GitBranch
} from 'lucide-react'
import { useNotes } from '../../contexts/NotesContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useTabs } from '../../contexts/TabsContext'
import localStorageService from '../../services/localStorageService'
import { getDefaultNoteData, getNoteTypeLabel } from '../../utils/noteTypeDetection'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import StatusBar from '../../components/common/StatusBar'
import FolderTabBar from '../../components/folders/FolderTabBar'
import NewFolderButton from '../../components/folders/NewFolderButton'
import NoteFolderDropdown from '../../components/folders/NoteFolderDropdown'
import CloudReconnectionBanner from '../../components/cloud/CloudReconnectionBanner'
// Removed SensitiveNotePasswordModal import - using browser popup instead
import { useAuth } from '../../contexts/AuthContext'
import { useStatus } from '../../contexts/StatusContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'
import toast from 'react-hot-toast'

/**
 * Notes Page Component
 * 
 * Displays all user notes with options to create, view, edit, and delete.
 * Includes search functionality and pagination.
 * 
 * @param {Function} onNoteOpen - Callback function called when a note is opened (for mobile navigation)
 */
const NotesPage = ({ onNoteOpen }) => {
  const navigate = useNavigate()
  const { user, openSettings } = useAuth()
  const { currentStatus, hideStatus, showSuccess, showError, showInfo, showLoading } = useStatus()
  const notesContext = useNotes()
  const { 
    notes = [], 
    loading, 
    pagination, 
    fetchNotes, 
    loadMore,
    hasMoreNotes,
    deleteNote, 
    searchNotes, 
    showReconnectionBanner, 
    hideReconnectionBanner,
    // Real-time search functions
    searchAsYouType,
    clearSearch,
    isSearchMode,
    currentSearchTerm,
    searchLoading,
    highlightSearchTerm,
    getSearchSnippet
  } = notesContext || {}
  const { 
    folders = [], 
    currentFolder, 
    isShowingAllNotes, 
    selectFolder, 
    showAllNotes,
    deleteCustomFolder,
    fetchFolders,
    loading: foldersLoading
  } = useFolders()
  const { loading: localLoading, isLocalMode, getSensitivePassword } = useLocalMode()
  const { tabs, openTab } = useTabs()
  const { isAuthenticated: isSensitiveAuthenticated, authenticate: authenticateSensitive } = useSensitiveSession()

  const [searchTerm, setSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  // Removed modal state - using browser popup instead
  const [pendingNote, setPendingNote] = useState(null)
  const [pendingAction, setPendingAction] = useState('view') // 'view' or 'edit'

  // Close dropdown when switching to sensitive folder
  useEffect(() => {
    if (currentFolder?.folderType === 'SENSITIVE') {
      setShowDropdown(false)
    }
  }, [currentFolder])

  // Safety check to ensure notes and folders are always arrays
  const safeNotes = Array.isArray(notes) ? notes : []
  const safeFolders = Array.isArray(folders) ? folders : []

  /**
   * Check if content appears to be encrypted
   */
  const isContentEncrypted = (content) => {
    if (!content) return false
    
    // Check if content looks like encrypted data (AES-GCM produces base64-like strings)
    const looksLikeHTML = content.includes('<') && content.includes('>')
    const looksLikePlainText = content.length < 100 && /^[a-zA-Z0-9\s.,!?-]+$/.test(content)
    const looksLikeEncrypted = content.length > 50 && /^[A-Za-z0-9+/]*={0,2}$/.test(content)
    
    // If it looks like encrypted data and doesn't look like HTML or plain text, it's likely encrypted
    return looksLikeEncrypted && !looksLikeHTML && !looksLikePlainText
  }

  /**
   * Check if a note is sensitive (in sensitive folder)
   */
  const isSensitiveNote = (note) => {
    if (!note.folderId) return false
    
    const noteFolder = folders.find(f => f.folderId === note.folderId)
    return noteFolder?.folderType === 'SENSITIVE'
  }

  /**
   * Get displayable content for note (decrypt if sensitive)
   */
  const getDisplayableContent = (note) => {
    if (!note.content) return ''
    
    // Check if note is in a sensitive folder
    if (note.folderId) {
      const noteFolder = folders.find(f => f.folderId === note.folderId)
      if (noteFolder?.folderType === 'SENSITIVE') {
        // For sensitive notes, we'll show encrypted content as placeholder
        // The actual decryption will happen when the note is opened
              if (isContentEncrypted(note.content)) {
          return '🔒 This note is encrypted. Click to view and enter decryption password.'
              } else {
                // Content appears to be plain text, return as-is
                return note.content
              }
      }
    }
    
    // Regular note content
    return note.content
  }

  // Fetch notes when component mounts or folder changes
  useEffect(() => {
    let folderId = null
    
    if (!isShowingAllNotes && currentFolder) {
      folderId = currentFolder.folderId
    } else if (isShowingAllNotes) {
      folderId = 'all' // Special value for all notes except sensitive
    }
    
    fetchNotes({ folderId, page: 0 }) // Reset to page 0 when changing folders
  }, [currentFolder, isShowingAllNotes])

  /**
   * Handle folder tab selection
   */
  const handleFolderSelect = (folder) => {
    selectFolder(folder)
  }

  /**
   * Handle "All Notes" tab selection
   */
  const handleShowAllNotes = () => {
    showAllNotes()
  }

  /**
   * Get count of all notes (for "All Notes" tab)
   */
  const getAllNotesCount = () => {
    return pagination?.totalElements || safeNotes.length
  }

  /**
   * Handle note move callback to refresh the notes list and folder counts
   */
  const handleNoteMove = async () => {
    let folderId = null
    if (!isShowingAllNotes && currentFolder) {
      folderId = currentFolder.folderId
    } else if (isShowingAllNotes) {
      folderId = 'all'
    }
    
    // Refresh both notes and folder data to update counts
    await Promise.all([
      fetchNotes({ folderId, page: 0 }),
      fetchFolders() // This will update the folder note counts
    ])
  }

  /**
   * Get folder name for a note
   */
  const getFolderNameByNote = (note) => {
    if (!note.folderId) return null
    
          // Check if folders are still loading (either local mode or cloud mode)
      if (safeFolders.length === 0 && (foldersLoading || localLoading)) {
        return '...' // Show loading indicator instead of "Unknown"
      }
      
      const folder = safeFolders.find(f => f.folderId === note.folderId)
    return folder?.name || 'Unknown'
  }

  /**
   * Get folder icon for a note
   */
  const getFolderIconByNote = (note) => {
    if (!note.folderId) return null
    const folder = safeFolders.find(f => f.folderId === note.folderId)
    if (!folder) return <Folder size={12} color="#6B7280" />
    
    const iconProps = {
      size: 12,
      className: 'flex-shrink-0'
    }
    
    switch (folder.folderType) {
      case 'PINNED':
        return <Pin {...iconProps} color="#FFD700" />
      case 'SENSITIVE':
        return <Shield {...iconProps} color="#DC2626" />
      case 'CUSTOM':
      default:
        return <Folder {...iconProps} color={folder.color || '#6B7280'} />
    }
  }

  /**
   * Check if current folder is empty and deletable
   */
  const canDeleteCurrentFolder = () => {
    // console.log('🔍 canDeleteCurrentFolder: Checking if folder can be deleted')
    // console.log('  - isShowingAllNotes:', isShowingAllNotes)
    // console.log('  - currentFolder:', currentFolder)
    // console.log('  - notes.length:', notes.length)
    
    // Must be viewing a specific folder (not "All Notes")
    if (isShowingAllNotes || !currentFolder) {
      // console.log('  ❌ Not viewing a specific folder')
      return false
    }
    
    // Must be a custom folder (not default Pinned/Sensitive)
    // Check for both isDefault flag and folderType
    const isDefaultFolder = currentFolder.isDefault || 
                          currentFolder.folderType === 'PINNED' || 
                          currentFolder.folderType === 'SENSITIVE'
    
    // console.log('  - currentFolder.isDefault:', currentFolder.isDefault)
    // console.log('  - currentFolder.folderType:', currentFolder.folderType)
    // console.log('  - isDefaultFolder:', isDefaultFolder)
    
    if (isDefaultFolder) {
      // console.log('  ❌ Cannot delete default folder')
      return false
    }
    
          // Must be empty (no notes)
      const isEmpty = safeNotes.length === 0
      //console.log('  - isEmpty:', isEmpty)
      
      const canDelete = isEmpty
      //console.log('  ✅ canDelete:', canDelete)
      
      return canDelete
  }

  /**
   * Handle folder deletion
   */
  const handleDeleteFolder = async () => {
    if (!currentFolder || !canDeleteCurrentFolder()) return

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the folder "${currentFolder.name}"? This action cannot be undone.`
    )
    
    if (!confirmDelete) return

    try {
      await deleteCustomFolder(currentFolder.folderId)
      // Switch back to "All Notes" view after deletion
      showAllNotes()
    } catch (error) {
      console.error('Failed to delete folder:', error)
      toast.error('Failed to delete folder')
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.note-type-dropdown')) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  /**
   * Handle search input change (real-time search as you type)
   */
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value
    setSearchTerm(newSearchTerm)
    
    // Get current folder context
    let folderId = null
    if (!isShowingAllNotes && currentFolder) {
      folderId = currentFolder.folderId
    } else if (isShowingAllNotes) {
      folderId = 'all'
    }
    
    // Trigger real-time search
    searchAsYouType(newSearchTerm, folderId)
  }

  /**
   * Handle search form submit (fallback for users who press Enter)
   */
  const handleSearch = async (e) => {
    e.preventDefault()
    // Real-time search already handled in handleSearchChange
    // This is just a fallback for form submission
  }

  /**
   * Clear search and return to normal mode
   */
  const clearSearchAndReload = () => {
    setSearchTerm('')
    clearSearch() // Use NotesContext clearSearch to exit search mode
  }

  /**
   * Get note ID from note object (handles different field names)
   */
  const getNoteId = (note) => {
    // Use noteId as the primary identifier
    return note.noteId
  }

  /**
   * Handle note deletion
   */
  const handleDelete = async (noteId, noteTitle) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${noteTitle}"? This action cannot be undone.`
    )
    
    if (!confirmDelete) return

    try {
      await deleteNote(noteId)
      // Refresh folder data to update note counts
      await fetchFolders()
    } catch (error) {
      toast.error('Failed to delete note')
    }
  }

  /**
   * Handle note view in tab
   */
  const handleViewNote = (note) => {
    const noteId = getNoteId(note)
    
    if (noteId) {
      // Check if this is a sensitive note that needs authentication
      if (note.folderId) {
        const noteFolder = folders.find(f => f.folderId === note.folderId)
        if (noteFolder?.folderType === 'SENSITIVE' && isContentEncrypted(note.content)) {
          // Check if user is already authenticated for sensitive folder
          if (isSensitiveAuthenticated) {
            // User is authenticated, open the note directly
            openTab({
              type: 'view',
              noteId: noteId,
              title: (note.title && typeof note.title === 'string') ? note.title : 'Untitled Note',
              note: note,
              isSensitive: true
            })
          } else {
            // User is not authenticated, prompt for password using browser popup
            setPendingNote(note)
            setPendingAction('view')
            promptForPassword(note)
            return
          }
        } else {
          // Regular note or non-encrypted sensitive note
          openTab({
            type: 'view',
            noteId: noteId,
            title: (note.title && typeof note.title === 'string') ? note.title : 'Untitled Note',
            note: note
          })
        }
      } else {
        // Regular note
        openTab({
          type: 'view',
          noteId: noteId,
          title: (note.title && typeof note.title === 'string') ? note.title : 'Untitled Note',
          note: note
        })
      }
      
      // Notify parent component that a note was opened (for mobile navigation)
      onNoteOpen?.()
    } else {
      console.error('❌ NotesPage: No note ID found in note object:', note)
      toast.error('Unable to view note - invalid ID')
    }
  }

  /**
   * Prompt for password using browser popup
   */
  const promptForPassword = async (noteToDecrypt) => {
    try {
      const noteTitle = noteToDecrypt.title || 'this note'
      const action = pendingAction === 'edit' ? 'edit' : 'access'
      
      const password = window.prompt(
        `This note "${noteTitle}" is encrypted and requires your password to ${action}.\n\nEnter your password:`
      )
      
      if (password === null) {
        // User cancelled
        console.log('🔐 NotesPage: User cancelled password prompt')
        setPendingNote(null)
        setPendingAction('view')
        return
      }
      
      if (!password.trim()) {
        // Empty password
        toast.error('Password is required')
        // Retry the prompt
        setTimeout(() => {
          promptForPassword(noteToDecrypt)
        }, 100)
        return
      }
      
      try {
        await handlePasswordConfirm(password)
      } catch (error) {
        console.error('❌ NotesPage: Password authentication failed:', error)
        toast.error(error.message || 'Invalid password. Please try again.')
        // Retry the prompt
        setTimeout(() => {
          promptForPassword(noteToDecrypt)
        }, 100)
      }
    } catch (error) {
      console.error('❌ NotesPage: Error in password prompt:', error)
      toast.error('Failed to prompt for password')
    }
  }

  /**
   * Handle password confirmation for sensitive note access
   */
  const handlePasswordConfirm = async (password) => {
    try {
      if (isLocalMode) {
        // For local mode, authenticate with the sensitive session
        const { sensitiveAPI } = await import('../../services/api')
        const response = await sensitiveAPI.authenticateForAccess({ password })
        
        // Store the session
        authenticateSensitive(response.token, response.expiresAt, password)
        
        // Now open the pending note (determine if it was for view or edit based on context)
        if (pendingNote) {
          const noteId = getNoteId(pendingNote)
          openTab({
            type: pendingAction,
            noteId: noteId,
            title: pendingNote.title || 'Untitled Note',
            note: pendingNote,
            isSensitive: true
          })
          
          // Notify parent component that a note was opened
          onNoteOpen?.()
        }
      } else {
        // For cloud mode, authenticate with the sensitive session
        const { sensitiveAPI } = await import('../../services/api')
        const response = await sensitiveAPI.authenticateForAccess({ password })
        
        // Store the session
        authenticateSensitive(response.token, response.expiresAt, password)
        
        // Now open the pending note (determine if it was for view or edit based on context)
        if (pendingNote) {
          const noteId = getNoteId(pendingNote)
          openTab({
            type: pendingAction,
            noteId: noteId,
            title: pendingNote.title || 'Untitled Note',
            note: pendingNote,
            isSensitive: true
          })
          
          // Notify parent component that a note was opened
          onNoteOpen?.()
        }
      }
      
      // Clear pending note after successful authentication
      setPendingNote(null)
      setPendingAction('view')
    } catch (error) {
      console.error('❌ NotesPage: Password authentication failed:', error)
      throw error
    }
  }

  // Removed handlePasswordModalClose - using browser popup instead

  /**
   * Handle note edit in tab
   */
  const handleEditNote = (note) => {
    const noteId = getNoteId(note)
    console.log('🔍 NotesPage: Editing note with ID:', noteId)
    
    if (noteId) {
      // Check if this is a sensitive note that needs authentication
      if (note.folderId) {
        const noteFolder = folders.find(f => f.folderId === note.folderId)
        if (noteFolder?.folderType === 'SENSITIVE' && isContentEncrypted(note.content)) {
          // Check if user is already authenticated for sensitive folder
          if (isSensitiveAuthenticated) {
            // User is authenticated, open the note for editing directly
            openTab({
              type: 'edit',
              noteId: noteId,
              title: (note.title && typeof note.title === 'string') ? note.title : 'Untitled Note',
              note: note,
              isSensitive: true
            })
          } else {
            // User is not authenticated, prompt for password using browser popup
            setPendingNote(note)
            setPendingAction('edit')
            promptForPassword(note)
            return
          }
        } else {
          // Regular note or non-encrypted sensitive note
      openTab({
        type: 'edit',
        noteId: noteId,
        title: (note.title && typeof note.title === 'string') ? note.title : 'Untitled Note',
        note: note
      })
        }
      } else {
        // Regular note
        openTab({
          type: 'edit',
          noteId: noteId,
          title: (note.title && typeof note.title === 'string') ? note.title : 'Untitled Note',
          note: note
        })
      }
      
      // Notify parent component that a note was opened (for mobile navigation)
      onNoteOpen?.()
    } else {
      console.error('❌ NotesPage: No note ID found for edit:', note)
      toast.error('Unable to edit note - invalid ID')
    }
  }

  /**
   * Handle create new note in tab
   */
  const handleCreateNote = () => {
    // Generate a unique ID for each new note tab
    const uniqueId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Count existing new note tabs to create unique title
    const newNoteCount = tabs.filter(tab => tab.type === 'new').length + 1
    const uniqueTitle = `New Note ${newNoteCount}`
    
    // If in sensitive folder, create note in All Notes (no specific folder)
    const noteData = currentFolder?.folderType === 'SENSITIVE' ? { folderId: null } : null
    
    openTab({
      type: 'new',
      noteId: uniqueId,
      title: uniqueTitle,
      note: noteData
    })
    // Notify parent component that a note was opened (for mobile navigation)
    onNoteOpen?.()
  }

  /**
   * Handle creating different types of notes
   */
  const handleCreateNoteType = (noteType) => {
    setShowDropdown(false)
    
    // Generate a unique ID for each new note tab
    const uniqueId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Count existing new note tabs to create unique title
    const newNoteCount = tabs.filter(tab => tab.type === 'new').length + 1
    const noteTypeLabel = getNoteTypeLabel(noteType)
    const uniqueTitle = `${noteTypeLabel} ${newNoteCount}`
    
    // Create default note data for the specific type
    const defaultNoteData = getDefaultNoteData(noteType)
    
    // Open tab with single purpose note data
    openTab({
      type: 'new',
      noteId: uniqueId,
      title: uniqueTitle,
      note: defaultNoteData,
      noteType: noteType
    })
    
    // Show success message
    toast.success(`Creating ${noteTypeLabel}...`)
    
    // Notify parent component that a note was opened (for mobile navigation)
    onNoteOpen?.()
  }

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  /**
   * Strip HTML tags from rich text content - OPTIMIZED
   */
  const stripHtml = useMemo(() => {
    return (html) => {
      if (!html) return ''
      
      // PERFORMANCE FIX: Use regex instead of DOM parsing for better performance
      const processedHtml = html
        .replace(/<img[^>]+src="image:\/\/[^"]+"[^>]*>/g, '[Image]')  // Replace image refs
        .replace(/<[^>]*>/g, '')  // Strip all HTML tags
        .replace(/&nbsp;/g, ' ')  // Replace HTML entities
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
      
      return processedHtml
    }
  }, [])

  /**
   * Truncate text for preview (strips HTML first) - OPTIMIZED
   */
  const truncateText = useCallback((text, maxLength = 150) => {
    if (!text) return ''
    
    // Strip HTML tags first
    const plainText = stripHtml(text)
    
    if (plainText.length <= maxLength) return plainText
    return plainText.substring(0, maxLength) + '...'
  }, [stripHtml])

  /**
   * Get character count (strips HTML tags) - OPTIMIZED
   */
  const getPlainTextLength = useCallback((content) => {
    if (!content) return 0
    return stripHtml(content).length
  }, [stripHtml])

  /**
   * Get word count (strips HTML tags) - OPTIMIZED
   */
  const getContentWordCount = useCallback((content) => {
    if (!content) return 0;
    const plainText = stripHtml(content);
    return plainText.split(/\s+/).filter(word => word.length > 0).length;
  }, [stripHtml]);

  /**
   * Search-optimized note card with highlighting
   */
  const SearchResultCard = ({ note }) => {
    const noteId = getNoteId(note)
    const displayTitle = note.title || 'Untitled'
    const displayContent = getDisplayableContent(note) || ''
    
    // Get highlighted title and content snippet
    const highlightedTitle = isSearchMode && currentSearchTerm 
      ? highlightSearchTerm(displayTitle, currentSearchTerm)
      : displayTitle
      
    const contentSnippet = isSearchMode && currentSearchTerm
      ? getSearchSnippet(displayContent, currentSearchTerm)
      : displayContent.substring(0, 150) + (displayContent.length > 150 ? '...' : '')
      
    const highlightedContent = isSearchMode && currentSearchTerm
      ? highlightSearchTerm(contentSnippet, currentSearchTerm)
      : contentSnippet

    return (
      <div 
        key={`search-result-${noteId}-${Math.random()}`}
        className="group bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer"
        onClick={(e) => {
          console.log('🔍 SearchResultCard clicked!', { note, noteId, target: e.target })
          handleViewNote(note)
        }}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 
              className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors"
              dangerouslySetInnerHTML={{ __html: highlightedTitle }}
            />
            {highlightedContent && (
              <p 
                className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: highlightedContent }}
              />
            )}
            <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {note.lastModified 
                  ? new Date(note.lastModified).toLocaleDateString()
                  : 'No date'
                }
              </span>
              {note.folderId && (
                <span className="flex items-center">
                  {getFolderIconByNote(note)}
                  <span className="ml-1">{getFolderNameByNote(note)}</span>
                </span>
              )}
              {isSearchMode && currentSearchTerm && (
                <span className="flex items-center text-blue-600 dark:text-blue-400">
                  <SearchIcon className="h-3 w-3 mr-1" />
                  Match
                </span>
              )}
            </div>
          </div>
          <div className="ml-4 flex items-center space-x-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            <NoteFolderDropdown 
              note={note} 
              onNoteMove={handleNoteMove}
              className="p-2 rounded-full text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            />
            {!isSensitiveNote(note) && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleEditNote(note)
                }}
                className="p-2 rounded-full text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                title="Edit note"
              >
                <Edit3 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(noteId, note.title)
              }}
              className="p-2 rounded-full text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Delete note"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden w-full max-w-full">
      {/* Google Drive Reconnection Banner */}
      {!isLocalMode && (
        <CloudReconnectionBanner
          isVisible={showReconnectionBanner}
          onDismiss={hideReconnectionBanner}
        />
      )}
      
      {/* Status Bar */}
      <StatusBar 
        message={currentStatus.message}
        type={currentStatus.type}
        isVisible={currentStatus.isVisible}
        onDismiss={() => hideStatus()}
      />
      
      {/* Folder Tabs */}
      <div className="flex-shrink-0 w-full overflow-x-hidden">
        <FolderTabBar
          allNotesCount={getAllNotesCount()}
          onFolderSelect={handleFolderSelect}
          onShowAllNotes={handleShowAllNotes}
        />
      </div>

      {/* Quick Actions */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="note-type-dropdown relative">
          {/* Split Button Group */}
          <div className="flex w-full gap-1 flex-wrap">
            {/* Main New Note Button */}
            <button
              onClick={handleCreateNote}
              className="btn-primary flex-1 inline-flex items-center justify-center"
              title={currentFolder?.folderType === 'SENSITIVE' ? 'Create a new note in All Notes (not in sensitive folder)' : 'Create a new note'}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Note
            </button>
            
            {/* New Folder Button */}
            <NewFolderButton />
            
            {/* Dropdown Toggle Button */}
            {currentFolder?.folderType !== 'SENSITIVE' && (
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 px-3 py-2 rounded-lg transition-colors duration-200"
              title="More note types"
            >
              <MoreHorizontal className={`h-4 w-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
            </button>
            )}
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[60]">
              <div className="py-1">
                {currentFolder?.folderType === 'SENSITIVE' ? (
                  <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Note creation is disabled in sensitive folders
                  </div>
                ) : (
                  <>
                <button
                  onClick={() => handleCreateNoteType('FLOWCHART')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                >
                  <GitBranch className="h-4 w-4 mr-3 text-indigo-500 dark:text-indigo-400" />
                  Flowchart
                </button>
                <button
                  onClick={() => handleCreateNoteType('HANDWRITING')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                >
                  <Edit3 className="h-4 w-4 mr-3 text-purple-500 dark:text-purple-400" />
                  Handwriting Note
                </button>
                <button
                  onClick={() => handleCreateNoteType('VOICE')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                >
                  <Clock className="h-4 w-4 mr-3 text-red-500 dark:text-red-400" />
                  Voice Note
                </button>
                <button
                  onClick={() => handleCreateNoteType('ITEM_LIST')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                >
                  <List className="h-4 w-4 mr-3 text-blue-500 dark:text-blue-400" />
                  Item List
                </button>
                <button
                  onClick={() => handleCreateNoteType('PHOTO_NOTE')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                >
                  <FileText className="h-4 w-4 mr-3 text-green-500 dark:text-green-400" />
                  Photo Note
                </button>
                <button
                  onClick={() => handleCreateNoteType('SPREADSHEET')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                >
                  <Grid className="h-4 w-4 mr-3 text-orange-500 dark:text-orange-400" />
                  Spreadsheet Note
                </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search Mode Indicator */}
      {isSearchMode && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-700 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SearchIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                             <span className="text-sm text-blue-800 dark:text-blue-200">
                 {searchLoading ? 'Searching...' : `Found ${safeNotes.length} result${safeNotes.length !== 1 ? 's' : ''} for "${currentSearchTerm}"`}
               </span>
            </div>
            <button
              onClick={clearSearchAndReload}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm font-medium"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {searchLoading ? (
                <div className="animate-spin h-5 w-5 text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              ) : (
                <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              )}
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm"
              placeholder="Search notes..."
            />
            {searchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={clearSearchAndReload}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Clear search"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
                 {loading && safeNotes.length === 0 ? (
           <div className="flex justify-center items-center py-12">
             <LoadingSpinner size="lg" />
           </div>
         ) : safeNotes.length === 0 ? (
          /* Empty State */
          <div className="text-center py-8 px-4">
            <FileText className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {searchTerm ? 'No notes found' : 'No notes yet'}
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {searchTerm 
                ? 'Try adjusting your search terms.'
                : currentFolder?.folderType === 'SENSITIVE'
                  ? 'You can move notes from other folders to sensitive folder to encrypt them.'
                : 'Create your first note to get started.'
              }
            </p>
            <div className="mt-4">
              {searchTerm ? (
                <button
                  onClick={clearSearchAndReload}
                  className="btn-outline text-sm py-1.5 px-3"
                >
                  Clear Search
                </button>
              ) : (
                <div className="flex gap-2 justify-center items-center">
                  {currentFolder?.folderType !== 'SENSITIVE' && (
                  <button
                    onClick={handleCreateNote}
                    className="btn-primary text-sm py-1.5 px-3 inline-flex items-center"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Create Note
                  </button>
                  )}
                  
                  {/* Delete Folder Button - only show when folder is empty and deletable */}
                  {canDeleteCurrentFolder() && (
                    <button
                      onClick={handleDeleteFolder}
                      className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 hover:text-red-700 dark:hover:text-red-300 border border-red-300 dark:border-red-600 px-3 py-1.5 rounded-lg transition-colors duration-200 inline-flex items-center text-sm"
                      title={`Delete empty folder "${currentFolder?.name}"`}
                    >
                      <Archive className="h-3 w-3 mr-1" />
                      Delete Folder
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Notes List */
          <div 
            className={`p-3 ${isSearchMode ? 'space-y-4' : 'space-y-1'}`}
            onClick={(e) => {
              // Container click handler for debugging if needed
            }}
          >
                         {safeNotes.map((note, index) => {
               const noteId = getNoteId(note)
              
              // Use SearchResultCard for search results, regular card for normal browsing
              if (isSearchMode) {
                
                const displayTitle = note.title || 'Untitled'
                const displayContent = getDisplayableContent(note) || ''
                
                // Get highlighted title and content snippet
                const highlightedTitle = isSearchMode && currentSearchTerm 
                  ? highlightSearchTerm(displayTitle, currentSearchTerm)
                  : displayTitle
                  
                const contentSnippet = isSearchMode && currentSearchTerm
                  ? getSearchSnippet(displayContent, currentSearchTerm)
                  : displayContent.substring(0, 150) + (displayContent.length > 150 ? '...' : '')
                  
                const highlightedContent = isSearchMode && currentSearchTerm
                  ? highlightSearchTerm(contentSnippet, currentSearchTerm)
                  : contentSnippet

                return (
                  <div 
                    key={`search-${noteId || index}-${index}`}
                    className="group bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={(e) => {
                      handleViewNote(note)
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors"
                          dangerouslySetInnerHTML={{ __html: highlightedTitle }}
                        />
                        {highlightedContent && (
                          <p 
                            className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-3"
                            dangerouslySetInnerHTML={{ __html: highlightedContent }}
                          />
                        )}
                        <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {note.lastModified 
                              ? new Date(note.lastModified).toLocaleDateString()
                              : 'No date'
                            }
                          </span>
                          {note.folderId && (
                            <span className="flex items-center">
                              {getFolderIconByNote(note)}
                              <span className="ml-1">{getFolderNameByNote(note)}</span>
                            </span>
                          )}
                          {isSearchMode && currentSearchTerm && (
                            <span className="flex items-center text-blue-600 dark:text-blue-400">
                              <SearchIcon className="h-3 w-3 mr-1" />
                              Match
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 flex items-center space-x-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <NoteFolderDropdown 
                          note={note} 
                          onNoteMove={handleNoteMove}
                          className="p-2 rounded-full text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                        />
                        {!isSensitiveNote(note) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditNote(note)
                            }}
                            className="p-2 rounded-full text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="Edit note"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(noteId, note.title)
                          }}
                          className="p-2 rounded-full text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete note"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              }
              
              return (
                <div 
                  key={`note-${noteId || index}-${index}`} 
                  className="p-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors group"
                  onClick={(e) => {
                    handleViewNote(note)
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {note.title || 'Untitled'}
                      </h3>
                      {note.content && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {(() => {
                            const displayContent = getDisplayableContent(note)
                            const strippedContent = stripHtml(displayContent)
                            return strippedContent.substring(0, 100) + (strippedContent.length > 100 ? '...' : '')
                          })()}
                        </p>
                      )}
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-400 dark:text-gray-500">
                        <span>
                          {note.lastModified 
                            ? new Date(note.lastModified).toLocaleDateString()
                            : 'No date'
                          }
                        </span>
                        <span className="flex items-center">
                          <FileText className="h-3 w-3 mr-1" />
                          {getContentWordCount(getDisplayableContent(note))} words
                        </span>
                        {note.folderId && (
                          <span className="flex items-center">
                            {getFolderIconByNote(note)}
                            <span className="ml-1">{getFolderNameByNote(note)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity pointer-events-none">
                      <div className="pointer-events-auto">
                        <NoteFolderDropdown 
                          note={note} 
                          onNoteMove={handleNoteMove}
                          className="p-2 rounded-full text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                        />
                      </div>
                      {!isSensitiveNote(note) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditNote(note)
                          }}
                          className="p-2 rounded-full text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors pointer-events-auto"
                          title="Edit note"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(noteId, note.title)
                        }}
                        className="p-2 rounded-full text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors pointer-events-auto"
                        title="Delete note"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMoreNotes && !isSearchMode && pagination && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                disabled={loading}
                className="btn-primary py-2 px-6 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <span>Load More</span>
                                         <span className="text-xs opacity-75">
                       ({pagination.totalElements - safeNotes.length} remaining)
                     </span>
                  </>
                )}
              </button>
            </div>
                         <div className="text-center mt-2">
               <span className="text-xs text-gray-500 dark:text-gray-400">
                 Showing {safeNotes.length} of {pagination.totalElements} notes
               </span>
             </div>
          </div>
        )}
      </div>

      {/* Removed SensitiveNotePasswordModal - using browser popup instead */}
    </div>
  )
}

export default NotesPage 